import { useDeleteCustomLevelMutation } from '@/app/redux/endpoints/customLevels';
import { useUpdateStudentMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useCustomLevels } from '@/app/redux/hooks/customLevels';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  openConfirmationDialogue,
  setAttemptedProFeature,
  setConfirmationDialogueCallback,
  setContactFormModalOpen,
} from '@/app/redux/slices/modalsSlice';
import { CustomLevel } from '@/common/types/customLevel';
import {
  DashboardViewActions,
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import { FeatureSwitch } from '@/common/ui/FeatureGate';
import SearchInput from '@/common/ui/SearchInput';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { ProIcon } from '@/common/ui/UpgradeButton';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import {
  JoystickIcon,
  ListIcon,
  PlusIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { ToggleGroup, ToggleGroupItem } from '@shadcn/components/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CustomLevelCard from './CustomLevelCard';
import CustomLevelTableRow from './CustomLevelTableRow';

type ViewMode = 'gallery' | 'list';

export default function CustomLevelsView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentOrganisationId } = useCurrentOrganisationId();
  const { customLevels, isLoading } = useCustomLevels();
  const [deleteCustomLevel] = useDeleteCustomLevelMutation();
  const { students } = useStudents();
  const [updateStudent] = useUpdateStudentMutation();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(
    getFromLocalStorage('customlevels-view-mode') || 'gallery',
  );

  useEffect(() => {
    saveToLocalStorage('customlevels-view-mode', viewMode);
  }, [viewMode]);

  const filteredLevels = useMemo(() => {
    if (!searchTerm) return customLevels;
    return customLevels.filter((level: CustomLevel) =>
      (level.description || 'Untitled Level')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, customLevels]);

  function handleCreate() {
    trackMixpanelEvent('Custom Level Create Clicked');
    navigate('/dashboard/customlevels/new');
  }

  function handleConfirmDelete(level: CustomLevel) {
    setConfirmationDialogueCallback(async () => {
      await handleDelete(level);
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Delete Custom Level',
        description: `Are you sure you want to delete "${level.description || 'Untitled Level'}"?`,
        variant: 'destructive',
      }),
    );
  }

  async function handleDelete(level: CustomLevel) {
    try {
      const assignedStudents = students.filter(
        (s) => s.customLevelId === level.id,
      );
      await Promise.all([
        deleteCustomLevel({
          orgId: currentOrganisationId!,
          id: level.id,
        }).unwrap(),
        ...assignedStudents.map((s) =>
          updateStudent({
            orgId: currentOrganisationId!,
            studentId: s.id,
            updates: { customLevelId: null },
          }).unwrap(),
        ),
      ]);
      trackMixpanelEvent('Custom Level Deleted', {
        customLevelId: level.id,
        assignedStudentCount: assignedStudents.length,
      });
      toast.success('Custom level deleted');
    } catch {
      toast.error('Failed to delete custom level', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function GallerySkeleton() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <Skeleton className="h-32 w-full" />
            <div className="p-3">
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  function ListSkeleton() {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(6)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-5 w-40" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-8" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>
          Custom Levels
          <Badge className="px-1 text-xxs" variant="secondary">
            {customLevels.length}
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center pl-2">
                <Badge variant="outline" className="gap-2">
                  Beta
                  <Info size={14} />
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-[300px] leading-5">
                Custom levels allow you to select custom content for learners to
                play with in the game. This is a beta feature and will be
                updated regularly.
                <br />
                <br />
                If you encounter any issues, please{' '}
                <Button
                  variant="link"
                  onClick={() => dispatch(setContactFormModalOpen(true))}
                  className="font-medium text-[12px] h-fit p-0 m-0 "
                >
                  get in touch.
                </Button>
              </p>
            </TooltipContent>
          </Tooltip>
        </DashboardViewTitle>
        <DashboardViewActions>
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(value: ViewMode) => value && setViewMode(value)}
            className="border rounded-md"
          >
            <ToggleGroupItem
              value="gallery"
              aria-label="Gallery view"
              className="px-2"
            >
              <SquaresFourIcon size={18} />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="list"
              aria-label="List view"
              className="px-2"
            >
              <ListIcon size={18} />
            </ToggleGroupItem>
          </ToggleGroup>
          <SearchInput
            placeholder="Search custom levels..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <FeatureSwitch
            feature="custom-levels"
            enabled={
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleCreate}
              >
                <PlusIcon className="size-4" />
                Create Custom Level
              </Button>
            }
            disabled={
              <Button
                variant="outline"
                className="gap-2"
                onClick={() =>
                  dispatch(setAttemptedProFeature('Custom Levels'))
                }
              >
                <PlusIcon className="size-4" />
                Create Custom Level
                <ProIcon />
              </Button>
            }
          />
        </DashboardViewActions>
      </DashboardViewHeader>

      <DashboardViewContent>
        {isLoading ? (
          viewMode === 'gallery' ? (
            <GallerySkeleton />
          ) : (
            <ListSkeleton />
          )
        ) : filteredLevels.length > 0 ? (
          viewMode === 'gallery' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
              {filteredLevels.map((level) => (
                <CustomLevelCard
                  key={level.id}
                  customLevel={level}
                  onConfirmDelete={() => handleConfirmDelete(level)}
                />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLevels.map((level) => (
                  <CustomLevelTableRow
                    key={level.id}
                    customLevel={level}
                    onConfirmDelete={() => handleConfirmDelete(level)}
                  />
                ))}
              </TableBody>
            </Table>
          )
        ) : (
          <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
            <JoystickIcon
              size={80}
              className="text-neutral-400"
              weight="thin"
            />
            <div className="flex flex-col items-center gap-2">
              <h3 className="text-lg font-medium text-neutral-900">
                {searchTerm
                  ? 'No matching custom levels'
                  : 'No custom levels yet'}
              </h3>
              <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                {searchTerm
                  ? 'Try a different search term or create a new custom level.'
                  : 'Create a custom level to get started.'}
              </p>
            </div>
            <FeatureSwitch
              feature="custom-levels"
              enabled={
                <Button onClick={handleCreate} className="gap-2">
                  <PlusIcon className="size-4" />
                  Create Custom Level
                </Button>
              }
              disabled={
                <Button
                  onClick={() =>
                    dispatch(setAttemptedProFeature('Custom Levels'))
                  }
                  className="gap-2"
                >
                  <PlusIcon className="size-4" />
                  Create Custom Level
                  <ProIcon />
                </Button>
              }
            />
          </div>
        )}
      </DashboardViewContent>
    </DashboardViewContainer>
  );
}
